import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { StripeSubscriptionsBlock as StripeSubscriptionsBlockType } from "../../../../shared/schema";
import { MobileList } from "../../MobileList";
import { useStripeSubscriptions, type StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import { useStripeSubscriptionMutation } from "../../../hooks/useStripeSubscriptionMutations";
import { useStripeSubscriptionsFilter } from "../../../hooks/useItemFilter";
import { SubscriptionCard } from "./SubscriptionCard";
import { SubscriptionCardSkeleton } from "./SubscriptionCardSkeleton";
import { SubscriptionDetailDialog } from "./SubscriptionDetailDialog";

interface StripeSubscriptionsBlockProps {
  block: StripeSubscriptionsBlockType;
}

function SubscriptionListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <SubscriptionCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function StripeSubscriptionsBlock({ block }: StripeSubscriptionsBlockProps) {
  const { t } = useTranslation("payments");
  const [selectedSubscription, setSelectedSubscription] = useState<StripeSubscription | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  const {
    subscriptions: rawSubscriptions,
    isLoading,
    error,
  } = useStripeSubscriptions({
    limit: block.props.maxItems ?? 5,
  });

  const { filteredItems: subscriptions, isLoading: filterLoading } = useStripeSubscriptionsFilter({
    items: rawSubscriptions,
    itemFilter: block.props.itemFilter,
  });

  // Mutation hooks for quick actions (separate instances for independent pending states)
  const resumeMutation = useStripeSubscriptionMutation();
  const undoCancelMutation = useStripeSubscriptionMutation();

  const isAnyMutationPending = resumeMutation.isPending || undoCancelMutation.isPending;

  const handleManageClick = (subscription: StripeSubscription) => {
    setSelectedSubscription(subscription);
    setIsDetailDialogOpen(true);
  };

  const handleQuickAction = (subscription: StripeSubscription, action: "resume" | "update_payment" | "undo_cancel") => {
    switch (action) {
      case "resume":
        resumeMutation.mutate(
          {
            integration: "Stripe",
            type: "subscription-resume",
            id: subscription.id,
          },
          {
            onSuccess: () => toast.success(t("Welcome back! Your subscription is active.")),
            onError: () => toast.error(t("Failed to resume subscription. Please try again.")),
          }
        );
        break;

      case "undo_cancel":
        undoCancelMutation.mutate(
          {
            integration: "Stripe",
            type: "subscription-undo-cancel",
            id: subscription.id,
          },
          {
            onSuccess: () => toast.success(t("Great! Your subscription will continue.")),
            onError: () => toast.error(t("Failed to restore subscription. Please try again.")),
          }
        );
        break;

      case "update_payment":
        // Open the detail dialog with payment update focus
        setSelectedSubscription(subscription);
        setIsDetailDialogOpen(true);
        break;
    }
  };

  const handleDetailDialogClose = () => {
    setIsDetailDialogOpen(false);
    setSelectedSubscription(null);
  };

  if (isLoading || filterLoading) {
    return <SubscriptionListSkeleton count={block.props.maxItems ?? 5} />;
  }

  // Log error for debugging, but show empty state to avoid alarming users
  if (error) {
    console.error("Failed to load Stripe subscriptions:", error);
  }

  return (
    <>
      <MobileList
        items={subscriptions}
        renderItem={(subscription) => (
          <SubscriptionCard
            subscription={subscription}
            blockProps={block.props}
            onManageClick={handleManageClick}
            onQuickAction={handleQuickAction}
            isActionPending={isAnyMutationPending}
          />
        )}
        title={t("Your Subscriptions")}
        maxItems={block.props.maxItems ?? 5}
        emptyMessage={block.props.emptyMessage ?? t("No subscriptions")}
      />

      {selectedSubscription && (
        <SubscriptionDetailDialog
          subscription={selectedSubscription}
          blockProps={block.props}
          open={isDetailDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleDetailDialogClose();
            }
          }}
        />
      )}
    </>
  );
}
